import React, { createContext, useState, useCallback, useMemo } from "react";
import { TasksService } from "../services/TasksService";
import { PartnersService } from "../services/PartnersService";
import { Task, TaskType } from "../types";
import { SponsorsService } from "../services/SponsorsService";
import { useNotificationStore } from "../../../Store/NotificationStore";
import { useTasksStore } from "../../../Store/TasksStore";

interface TasksContextType {
  tasks: Task[];
  loading: boolean;
  error: Error | null;
  fetchTasks: (type: TaskType, groupId?: number) => Promise<void>;
  fetchGroupTasks: (groupId: number) => Promise<void>;
  claimReward: (taskId: number | string) => Promise<void>;
}

export const TasksContext = createContext<TasksContextType>({
  tasks: [],
  loading: false,
  error: null,
  fetchTasks: async () => {},
  fetchGroupTasks: async () => {},
  claimReward: async () => {},
});

export const TasksProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentType, setCurrentType] = useState<TaskType | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<number | undefined>(
    undefined
  );

  // Получаем функцию setNotification из хранилища уведомлений
  const setNotification = useNotificationStore(
    (state) => state.setNotification
  );

  const tasksForSponsors = useTasksStore((state) => state.tasks);
  const isLoadingFromStore = useTasksStore((state) => state.isLoading);

  // Отладочная информация
  console.log(
    "🔍 TasksProvider - tasksForSponsors из стора:",
    tasksForSponsors
  );
  console.log("🔍 TasksProvider - isLoadingFromStore:", isLoadingFromStore);

  const fetchTasks = useCallback(
    async (type: TaskType, groupId?: number) => {
      console.log("🚀 TasksProvider.fetchTasks - start", { type, groupId });

      try {
        setLoading(true);
        setCurrentType(type);
        setCurrentGroupId(groupId || undefined);

        let fetchedTasks;

        if (type === "sponsors") {
          // Для спонсорских заданий ждем, пока данные загрузятся в стор
          if (isLoadingFromStore) {
            console.log("⏳ Ждем загрузки данных из стора...");
            // Ждем, пока загрузка завершится
            while (isLoadingFromStore) {
              await new Promise((resolve) => setTimeout(resolve, 50));
            }
          }
          fetchedTasks = tasksForSponsors || [];
        } else {
          fetchedTasks = await TasksService.getTasks(type, groupId);
        }

        console.log("✨ TasksProvider.fetchTasks - fetchedTasks", fetchedTasks);
        setTasks(fetchedTasks);
      } catch (err) {
        console.error("❌ TasksProvider.fetchTasks - error", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setLoading(false);
      }
    },
    [tasksForSponsors, isLoadingFromStore]
  );

  const fetchGroupTasks = useCallback(async (groupId: number) => {
    console.log("🚀 TasksProvider.fetchGroupTasks - start", { groupId });
    try {
      setLoading(true);
      const tasks = await PartnersService.getPartnerTasks(groupId);
      setTasks(tasks);
    } catch (err) {
      console.error("❌ TasksProvider.fetchGroupTasks - error", err);
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setLoading(false);
    }
  }, []);

  const claimReward = async (taskId: number | string) => {
    if (!currentType) {
      console.error("❌ TasksProvider.claimReward - No current task type");
      return;
    }

    try {
      // Если это спонсорское задание
      if (currentType === "sponsors") {
        const success = await SponsorsService.claimSponsorTask(taskId);

        if (success === "WAITING") {
          setTasks((prev) => {
            const taskIndex = prev.findIndex((task) => task.id === taskId);
            if (taskIndex === -1) return prev;

            const newTasks = [...prev];
            newTasks[taskIndex] = {
              ...prev[taskIndex],
              status: "WAITING",
            };

            return newTasks;
          });

          return {
            task_id: taskId,
            status: "WAITING",
          };
        } else if (success === "DONE") {
          setTasks((prev) => {
            const taskIndex = prev.findIndex((task) => task.id === taskId);
            if (taskIndex === -1) return prev;

            const newTasks = [...prev];
            newTasks[taskIndex] = {
              ...prev[taskIndex],
              status: "DONE",
            };

            return newTasks;
          });

          return {
            task_id: taskId,
            status: "DONE",
          };
        } else if (success) {
          // Обновляем состояние задания в интерфейсе
          setTasks((prev) => {
            const taskIndex = prev.findIndex((task) => task.id === taskId);
            if (taskIndex === -1) return prev;

            const newTasks = [...prev];
            newTasks[taskIndex] = {
              ...prev[taskIndex],
              status: "CLAIMED",
            };

            return newTasks;
          });

          return {
            task_id: taskId,
            status: "CLAIMED",
          };
        }
        return null;
      }

      // Для обычных заданий - существующая логика
      const response = await TasksService.claimReward(
        currentType,
        taskId,
        currentGroupId
      );
      console.log("🚀 Response from claimReward:", response);

      // Обновляем список заданий после получения награды
      if (currentType === "partners" && currentGroupId) {
        const updatedTasks = await PartnersService.getPartnerTasks(
          currentGroupId
        );
        setTasks(updatedTasks);
      }

      if (
        response &&
        (response.status === "CLAIMED" ||
          (response.type === "tickets" &&
            response.amount &&
            response.new_balance))
      ) {
        setTasks((prev) => {
          const taskIndex = prev.findIndex((task) => task.id === taskId);
          if (taskIndex === -1) return prev;

          const newTasks = [...prev];
          newTasks[taskIndex] = {
            ...prev[taskIndex],
            status: "CLAIMED",
            ...(response.rewards_processed && {
              rewards: {
                ...prev[taskIndex].rewards,
              },
            }),
          };

          return newTasks;
        });

        // Отображаем сообщение об успешном получении награды
        if (
          response.rewards_processed &&
          response.rewards_processed.length > 0
        ) {
          const prize = response.rewards_processed[0];

          // Нормализуем тип награды
          let rewardType = prize.type;
          if (rewardType === "ticket") rewardType = "tickets";
          if (rewardType === "dc") rewardType = "DC";

          setNotification({
            type: "Success",
            message: "Награда получена!",
            rewards: [
              {
                amount: prize.amount,
                type: rewardType,
              },
            ],
          });
        } else if (response.type === "tickets" && response.amount) {
          setNotification({
            type: "Success",
            message: "Награда получена!",
            rewards: [
              {
                amount: response.amount,
                type: "tickets",
              },
            ],
          });
        } else {
          setNotification({
            type: "Success",
            message: "Награда успешно получена!",
          });
        }

        if (response.type === "tickets" && response.amount) {
          return {
            task_id: taskId,
            status: "CLAIMED",
            rewards_processed: [
              {
                type: response.type,
                amount: response.amount,
              },
            ],
          };
        }
      }
      return response;
    } catch (err) {
      console.error("❌ TasksProvider.claimReward - error", err);
      setError(err instanceof Error ? err : new Error("Unknown error"));

      // Используем новую систему уведомлений
      setNotification({
        type: "Error",
        message: "Не удалось получить награду. Попробуй позже",
      });

      throw err;
    }
  };

  const value = useMemo(
    () => ({
      tasks,
      loading,
      error,
      fetchTasks,
      fetchGroupTasks,
      claimReward,
    }),
    [tasks, loading, error, fetchTasks, fetchGroupTasks, claimReward]
  );

  return (
    <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
  );
};
